import { useCallback, useEffect, useState } from 'react';
import { pasteCopiedDoc } from '../../../../utils/pasteDoc';
import { isAtlasUnfiledId } from '../constants';

/**
 * Create / rename / paste / browse / delete actions for Atlas folders.
 */
export function useAtlasFolderActions({
  useDemo,
  organization,
  onCreateDoc,
  createDoc,
  onBrowseFolder,
  nodes,
  selectedIds,
  setSelectedIds,
  setExpandedId,
  setCtx,
  ensureRectInView,
  requestDelete,
  setNodeHeights,
}) {
  const [renamingId, setRenamingId] = useState(null);

  const handleCreateRootFolder = useCallback(() => {
    if (useDemo || !organization?.createFolder) return;
    const id = organization.createFolder('New folder', null);
    setCtx(null);
    if (id) {
      setSelectedIds(new Set([id]));
      setExpandedId(null);
      setRenamingId(id);
    }
  }, [useDemo, organization, setCtx, setSelectedIds, setExpandedId]);

  useEffect(() => {
    if (!renamingId) return;
    const node = nodes.find((n) => n.id === renamingId);
    if (!node) return;
    ensureRectInView({
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height,
    });
  }, [renamingId, nodes, ensureRectInView]);

  const handleCreateChild = useCallback(
    (parentId) => {
      if (useDemo || !organization?.createFolder) return;
      const id = organization.createFolder('New folder', parentId);
      setCtx(null);
      if (id) {
        setSelectedIds(new Set([id]));
        setExpandedId(null);
        setRenamingId(id);
      }
    },
    [useDemo, organization, setCtx, setSelectedIds, setExpandedId]
  );

  const handleCreateDocInFolder = useCallback(
    async (folderId) => {
      setCtx(null);
      if (!onCreateDoc) return;
      const doc = await onCreateDoc();
      if (doc?.id && organization?.moveDocToFolder && !useDemo) {
        organization.moveDocToFolder(
          doc.id,
          isAtlasUnfiledId(folderId) ? null : folderId
        );
      }
    },
    [onCreateDoc, organization, useDemo, setCtx]
  );

  const handlePasteDoc = useCallback(
    async (folderId) => {
      setCtx(null);
      if (!createDoc || useDemo) return;
      await pasteCopiedDoc({
        createDoc,
        moveDocToFolder: organization?.moveDocToFolder,
        folderId: isAtlasUnfiledId(folderId) ? null : folderId || null,
      });
    },
    [createDoc, organization, useDemo, setCtx]
  );

  const handleRename = useCallback((folderId) => {
    if (!folderId || isAtlasUnfiledId(folderId)) return;
    setCtx(null);
    setExpandedId(null);
    setRenamingId(folderId);
  }, [setCtx, setExpandedId]);

  const handleRenameCommit = useCallback(
    (folderId, name) => {
      setRenamingId(null);
      setNodeHeights((prev) => {
        if (!prev.has(folderId)) return prev;
        const next = new Map(prev);
        next.delete(folderId);
        return next;
      });
      organization?.renameFolder?.(folderId, name);
    },
    [organization, setNodeHeights]
  );

  const handleOpenBrowse = useCallback(
    (folderId) => {
      setExpandedId(null);
      setCtx(null);
      onBrowseFolder?.(folderId);
    },
    [onBrowseFolder, setExpandedId, setCtx]
  );

  const handleDeleteFromMenu = useCallback(
    (folderId) => {
      const ids = (
        selectedIds.has(folderId) && selectedIds.size > 1
          ? [...selectedIds]
          : [folderId]
      ).filter((id) => !isAtlasUnfiledId(id));
      requestDelete(ids);
    },
    [selectedIds, requestDelete]
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'F2' || renamingId) return;
      if (e.target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      const id = [...selectedIds].find((fid) => !isAtlasUnfiledId(fid));
      if (id) handleRename(id);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedIds, renamingId, handleRename]);

  return {
    renamingId,
    setRenamingId,
    handleCreateRootFolder,
    handleCreateChild,
    handleCreateDocInFolder,
    handlePasteDoc,
    handleRename,
    handleRenameCommit,
    handleOpenBrowse,
    handleDeleteFromMenu,
  };
}
